import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ActiveCartService, StateWithMultiCart } from '@spartacus/cart/base/core';
import { MultiCartFacade, OrderEntry } from '@spartacus/cart/base/root';
import { GlobalMessageService, GlobalMessageType, TranslationService, UserIdService, WindowRef } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { skip, switchMap, take, tap } from 'rxjs/operators';

interface UigOrderEntry extends OrderEntry {
  backendMessages?: UigBackendMessage[];
}

interface UigBackendMessage {
  code?: string;
  description?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UigActiveCartService extends ActiveCartService {
  private entries: UigOrderEntry | UigOrderEntry[];
  private entriesSubscription = new Subscription();

  constructor(
    protected store: Store<StateWithMultiCart>,
    protected multiCartFacade: MultiCartFacade,
    protected userIdService: UserIdService,
    protected winRef: WindowRef,
    private translationService: TranslationService,
    private globalMessageService: GlobalMessageService,
  ) {
    super(multiCartFacade, userIdService, winRef);
  }


  public addEntry(productCode: string, quantity: number, pickupStore?: string, showConfirmationMsg: boolean = true): void {
    super.addEntry(productCode, quantity);

    const SHOW_MSG_TIMEOUT_CONFIRMATION = 600000;

    this.entriesSubscription = super.getEntries().pipe(skip(1)).subscribe(entries => {
      this.entries = entries;
      this.sendCartModificationMsg(null, productCode);
    });

    this.translationService
      .translate('uigAddToCart.addingProductToCart', { productCode})
      .pipe(take(1))
      .subscribe((message) => {
        if (showConfirmationMsg) {
          this.globalMessageService.add(message, GlobalMessageType.MSG_TYPE_CONFIRMATION, SHOW_MSG_TIMEOUT_CONFIRMATION);
        }
        setTimeout(() => {
          this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_CONFIRMATION);
        }, SHOW_MSG_TIMEOUT_CONFIRMATION);
      });
  }

  public updateEntry(
    entryNumber: number,
    quantity: number
  ): Observable<UigOrderEntry | UigOrderEntry[]> {
    super.updateEntry(entryNumber, quantity);
    return super.getEntries().pipe(
      skip(1),
      take(1),
      switchMap((entries) => {
        return entries;
      }),
      tap((entries) => {
        this.entries = entries;
        this.sendCartModificationMsg(entryNumber);
      })
    );
  }

  public sendCartModificationMsg(entryNumber?: number, productCode?: string) {
    const SHOW_MSG_TIMEOUT_WARNING = 600000;

    if (this.entries instanceof Array) {
      this.entries.forEach((entry) => {
        this.displayMessage(
          entry,
          entryNumber,
          productCode,
          SHOW_MSG_TIMEOUT_WARNING
        );
      });
    } else {
      this.displayMessage(
        this.entries,
        entryNumber,
        productCode,
        SHOW_MSG_TIMEOUT_WARNING
      );
    }
    this.entriesSubscription.unsubscribe();
  }

  private displayMessage(
    entry: UigOrderEntry,
    entryNumber: number,
    productCode: string,
    SHOW_MSG_TIMEOUT_WARNING: number
  ) {
    const beMsg: UigBackendMessage[] = entry.backendMessages;
    if (entry.product) {
      if (
        beMsg && beMsg.length > 0 &&
        (entry.entryNumber === entryNumber ||
          entry.product.code === productCode)
      ) {
        beMsg.forEach((item) => {
          this.translationService
            .translate('uigAddToCart.backendMessage', {
              productCode: entry.product.code,
              errDescription: item.description,
            })
            .pipe(take(1))
            .subscribe((message) => {
              this.globalMessageService.add(
                message,
                GlobalMessageType.MSG_TYPE_WARNING,
                SHOW_MSG_TIMEOUT_WARNING
              );
            });
        });
      }
    }
  }
}
